export const ORG_PHONE_PATTERN =
  /^\d{3}-\d{3}-\d{4}(?: ext \d{1,6})?$/;

export function formatOrgPhoneInput(value) {
  const raw = String(value || "");
  const allDigits = raw.replace(/\D/g, "");
  const mainDigits = allDigits.slice(0, 10);
  const extDigits = allDigits.slice(10, 16);

  let formatted = mainDigits;
  if (mainDigits.length > 6) {
    formatted = `${mainDigits.slice(0, 3)}-${mainDigits.slice(3, 6)}-${mainDigits.slice(6)}`;
  } else if (mainDigits.length > 3) {
    formatted = `${mainDigits.slice(0, 3)}-${mainDigits.slice(3)}`;
  }

  if (extDigits.length > 0) {
    return `${formatted} ext ${extDigits}`;
  }

  // With a full number and a trailing space / "ext" marker but no digits yet,
  // surface the "ext " prompt so hitting space opens the extension. Backspace
  // clearing of this empty marker is handled in the field's key handler.
  const hasOpenExtMarker =
    mainDigits.length === 10 && /(?:\s|e|x|t)+$/i.test(raw);
  if (hasOpenExtMarker) {
    return `${formatted} ext `;
  }
  return formatted;
}

/** True when the value carries an "ext" marker with no extension digits yet. */
export function hasEmptyExtMarker(value) {
  return /\bext\s*$/i.test(String(value || "")) && !/ext\s*\d/i.test(String(value || ""));
}

/** Drop a digitless "ext" marker (used before persisting). */
export function stripEmptyExtMarker(value) {
  return String(value || "").replace(/\s*ext\s*$/i, "").trim();
}

export function isValidOrgPhone(value) {
  return ORG_PHONE_PATTERN.test(String(value || "").trim());
}

/**
 * Overall RFC-5322-inspired shape check (single `@`, dot-separated domain
 * labels, no leading/trailing/double dots, valid label characters).
 */
export const ORG_EMAIL_PATTERN =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,24}$/i;

/**
 * Recognized top-level domains. Any two-letter ccTLD is accepted, plus the
 * common gTLDs below. This rejects plausible-looking typos such as the `comm`
 * in `circa.com.comm` that a pure format check would otherwise allow.
 */
export const KNOWN_GTLDS = new Set([
  "com",
  "org",
  "net",
  "edu",
  "gov",
  "mil",
  "int",
  "info",
  "biz",
  "name",
  "pro",
  "io",
  "co",
  "ai",
  "app",
  "dev",
  "tech",
  "xyz",
  "online",
  "site",
  "store",
  "cloud",
  "email",
  "inc",
  "llc",
  "ltd",
  "group",
  "agency",
  "studio",
  "design",
  "media",
  "news",
  "health",
  "finance",
  "solutions",
  "systems",
  "digital",
  "network",
  "company",
  "academy",
  "consulting",
]);

function isRecognizedTld(tld) {
  const value = String(tld || "").toLowerCase();
  if (/^[a-z]{2}$/.test(value)) return true; // any country-code TLD
  return KNOWN_GTLDS.has(value);
}

/**
 * Stricter than the permissive HTML email check: enforces the
 * `ORG_EMAIL_PATTERN` shape and requires a recognized TLD, rejecting typos like
 * `blah@circa.com.com` and `harry@circa.com.comm`.
 */
export function isValidOrgEmail(value) {
  const email = String(value || "").trim().toLowerCase();
  if (!email || email.length > 254) return false;
  if (!ORG_EMAIL_PATTERN.test(email)) return false;

  const [local, domain] = email.split("@");
  if (local.length > 64) return false;

  const labels = domain.split(".");
  if (labels.some((label) => label.length > 63)) return false;

  const topLevel = labels[labels.length - 1];
  const preceding = labels[labels.length - 2];
  if (topLevel === preceding) return false; // e.g. circa.com.com
  return isRecognizedTld(topLevel);
}
